import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faSync, faExclamationTriangle, faCheckCircle,
  faQuestionCircle, faExchangeAlt, faSpinner, faCodeCompare,
  faPlus, faTrash, faBug, faRedo, faCrosshairs
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';
import webFindingService from '../../services/webFindingService';
import ExchangePanel from './ExchangePanel';
import ResponseDiffView from './ResponseDiffView';

const SEVERITIES = ['critical', 'high', 'medium', 'low', 'informational'];
const urlPath = (u) => { try { return new URL(u).pathname; } catch { return u; } };
const urlHost = (u) => { try { return new URL(u).hostname; } catch { return undefined; } };

const formatBytes = (bytes) => {
  const neg = bytes < 0;
  const abs = Math.abs(bytes);
  if (abs === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(abs) / Math.log(k));
  return (neg ? '-' : '') + (Math.round(abs / Math.pow(k, i) * 100) / 100) + ' ' + sizes[i];
};

const AuthSmashCellModal = ({
  endpoint,         // { method, url, baselineExchangeId }
  profile,          // { _id, name, role }
  cell,             // { exchangeId, enforcement, statusCode, reason, contentLength }
  onClose,
  onSendToRepeater, // (exchangeId) => void
  onSendToIntruder, // (exchangeId) => void
  isDarkMode
}) => {
  const [baselineExchange, setBaselineExchange] = useState(null);
  const [testExchange, setTestExchange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Persisted view preferences (remembered across modal opens until changed)
  const [view, setView] = useState(() => localStorage.getItem('authsmash-cell-view') || 'diff'); // 'diff','split','baseline','test'
  const [sharedTab, setSharedTab] = useState(() => localStorage.getItem('authsmash-cell-tab') || 'request'); // 'request'|'response'
  const [sharedViewMode, setSharedViewMode] = useState(() => localStorage.getItem('authsmash-cell-viewmode') || 'pretty'); // pretty|raw|headers|render

  useEffect(() => { localStorage.setItem('authsmash-cell-view', view); }, [view]);
  useEffect(() => { localStorage.setItem('authsmash-cell-tab', sharedTab); }, [sharedTab]);
  useEffect(() => { localStorage.setItem('authsmash-cell-viewmode', sharedViewMode); }, [sharedViewMode]);

  // Create Finding form state
  const [showForm, setShowForm] = useState(false);
  const [severity, setSeverity] = useState(cell.enforcement === 'bypassed' ? 'high' : 'medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [excerpts, setExcerpts] = useState([]); // { side:'baseline'|'test', content }
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  useEffect(() => {
    loadExchanges();
  }, [cell.exchangeId, endpoint.baselineExchangeId]);

  const loadExchanges = async () => {
    setLoading(true);
    setError(null);
    try {
      const [baseline, test] = await Promise.all([
        axios.get(`/http-exchanges/${endpoint.baselineExchangeId}`),
        axios.get(`/http-exchanges/${cell.exchangeId}`)
      ]);
      setBaselineExchange(baseline.data);
      setTestExchange(test.data);
    } catch (err) {
      console.error('Failed to load exchanges:', err);
      setError(err.response?.data?.error || 'Failed to load request/response data');
    } finally {
      setLoading(false);
    }
  };

  // Actual profile names used, read from the stored exchanges (falls back to props)
  const baselineName = baselineExchange?.authsmashTest?.profileName || 'Baseline';
  const testName = testExchange?.authsmashTest?.profileName || profile.name;

  const openFindingForm = () => {
    const baseS = baselineExchange?.response?.statusCode;
    const testS = testExchange?.response?.statusCode;
    setTitle(`Authorization Bypass: ${endpoint.method} ${urlPath(endpoint.url)}`);
    setDescription(
      `Role "${profile.role}" (${testName}) accessed an endpoint intended for higher privilege.\n` +
      `Baseline (${baselineName}) returned HTTP ${baseS}; test (${testName}) returned HTTP ${testS}. ` +
      `${cell.reason || ''}`.trim() +
      `\n\nThe test session was granted access to data/functionality it should not be authorized for.`
    );
    setSeverity(cell.enforcement === 'bypassed' ? 'high' : 'medium');
    setShowForm(true);
  };

  // Capture the user's current text selection from the visible response and attach
  // it as an evidence excerpt. Called on mouseDown+preventDefault so focusing the
  // button does not clear the selection.
  const addSelection = (side) => {
    const text = (window.getSelection && window.getSelection().toString()) || '';
    const trimmed = text.trim();
    if (!trimmed) {
      toast.info(`Select some text in the ${side} response first, then click “+ ${side} excerpt”.`);
      return;
    }
    setExcerpts(prev => [...prev, { side, content: trimmed.slice(0, 4000) }]);
    toast.success(`Added ${side} excerpt (${trimmed.length} chars)`);
  };

  const submitFinding = async () => {
    if (!title.trim()) { toast.warning('Add a title first'); return; }
    setCreating(true);
    try {
      const evidence = [
        {
          type: 'note',
          title: 'AuthSmash comparison',
          content: `baseline (${baselineName}): HTTP ${baselineExchange?.response?.statusCode}, ${formatBytes(baselineExchange?.response?.contentLength || 0)}; ` +
                   `test (${testName}, role ${profile.role}): HTTP ${testExchange?.response?.statusCode}, ${formatBytes(testExchange?.response?.contentLength || 0)}. ${cell.reason || ''}`,
          exchangeId: cell.exchangeId
        },
        ...excerpts.map(ex => ({
          type: 'note',
          title: ex.side === 'baseline' ? `Baseline response excerpt (${baselineName})` : `Test response excerpt (${testName})`,
          content: ex.content,
          exchangeId: ex.side === 'baseline' ? endpoint.baselineExchangeId : cell.exchangeId
        }))
      ];
      const payload = {
        project: testExchange?.project,
        webTarget: testExchange?.webTarget,
        scenario: testExchange?.authsmashTest?.scenarioId,
        title: title.trim(),
        description,
        severity,
        confidence: 'firm',
        wstgTest: { testId: 'WSTG-ATHZ-02', testName: 'Testing for Bypassing Authorization Schema', categoryCode: 'ATHZ' },
        cweId: '285',
        cweName: 'Improper Authorization',
        endpoint: { method: endpoint.method, url: endpoint.url },
        httpExchanges: [cell.exchangeId, endpoint.baselineExchangeId].filter(Boolean),
        evidence,
        remediation: 'Enforce authorization server-side for every sensitive endpoint. Confirm the response actually exposes protected data rather than public content.',
        references: ['https://owasp.org/www-project-web-security-testing-guide/'],
        classification: { testId: 'web.authz.bypass', vulnType: 'web', host: urlHost(endpoint.url), dedupTag: `web.authz.bypass|${endpoint.url}|${profile.role}` },
        source: 'manual',
        status: 'new'
      };
      await webFindingService.create(payload);
      setCreated(true);
      setShowForm(false);
      toast.success('Finding created — see the Findings tab.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create finding');
    } finally {
      setCreating(false);
    }
  };

  const getEnforcementBadge = () => {
    const badges = {
      enforced: {
        icon: faTimes,
        color: 'text-green-500',
        bg: isDarkMode ? 'bg-green-900/20' : 'bg-green-50',
        text: 'ENFORCED (Access Denied)',
        desc: 'Authorization is working correctly - access was denied as expected'
      },
      bypassed: {
        icon: faCheckCircle,
        color: 'text-red-500',
        bg: isDarkMode ? 'bg-red-900/20' : 'bg-red-50',
        text: 'BYPASSED (Vulnerability)',
        desc: 'Authorization bypass detected - access was granted when it should have been denied'
      },
      partial: {
        icon: faQuestionCircle,
        color: 'text-yellow-500',
        bg: isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50',
        text: 'NEEDS REVIEW',
        desc: 'Response differs but enforcement is unclear - manual review recommended'
      }
    };

    const badge = badges[cell.enforcement] || badges.partial;

    // Compact single-line badge to preserve vertical space for the diff/panels
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded ${badge.bg}`}>
        <FontAwesomeIcon icon={badge.icon} className={`${badge.color} text-sm flex-shrink-0`} />
        <span className={`text-xs font-semibold ${badge.color} flex-shrink-0`}>{badge.text}</span>
        <span className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          — {badge.desc}
        </span>
        {cell.reason && (
          <span className={`text-[11px] font-mono ml-auto flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {cell.reason}
          </span>
        )}
      </div>
    );
  };


  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-7xl h-5/6 rounded-lg shadow-2xl flex flex-col ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center justify-between ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex-1">
            <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Request / Response Comparison
            </h2>
            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <span className={`inline-block px-1.5 py-0.5 rounded mr-2 ${
                endpoint.method === 'GET' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' :
                endpoint.method === 'POST' ? 'bg-green-100 text-green-700 dark:bg-green-900/30' :
                endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30' :
                endpoint.method === 'DELETE' ? 'bg-red-100 text-red-700 dark:bg-red-900/30' :
                'bg-gray-100 text-gray-700 dark:bg-gray-700'
              }`}>
                {endpoint.method}
              </span>
              {endpoint.url}
            </div>
            <div className={`text-[11px] mt-0.5 flex items-center gap-1.5 flex-wrap ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <span className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                baseline: {baselineName}
              </span>
              <FontAwesomeIcon icon={faExchangeAlt} className="text-[9px]" />
              <span className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                test: {testName}
              </span>
              <span className={`px-1.5 py-0.5 rounded ${
                profile.role === 'superadmin' ? 'bg-red-100 text-red-700 dark:bg-red-900/30' :
                profile.role === 'admin' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30' :
                profile.role === 'user' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' :
                'bg-gray-100 text-gray-700 dark:bg-gray-700'
              }`}>
                {profile.role}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`ml-4 p-2 rounded hover:bg-gray-700 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Enforcement Result + Response Comparison (compact, space-saving) */}
        <div className={`px-4 py-2 border-b space-y-1.5 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          {getEnforcementBadge()}

          {/* Response Comparison — single line of chips */}
          {!loading && baselineExchange && testExchange && (() => {
            const baseStatus = baselineExchange.response?.statusCode ?? 'N/A';
            const testStatus = testExchange.response?.statusCode ?? 'N/A';
            const baseSize = baselineExchange.response?.contentLength || 0;
            const testSize = testExchange.response?.contentLength || 0;
            const diff = testSize - baseSize;
            const diffPercent = baseSize > 0 ? Math.round((diff / baseSize) * 100) : 0;
            const statusDiffers = baseStatus !== testStatus;
            const sizeSimilar = Math.abs(diffPercent) < 5;
            const chip = isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
            const label = isDarkMode ? 'text-gray-500' : 'text-gray-400';
            const val = isDarkMode ? 'text-gray-300' : 'text-gray-700';
            return (
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className={`px-2 py-1 rounded border flex items-center gap-1.5 ${chip}`}>
                  <span className={label}>Status</span>
                  <span className={val}>{baseStatus}</span>
                  <FontAwesomeIcon icon={faExchangeAlt} className="text-gray-500 text-[9px]" />
                  <span className={statusDiffers ? 'text-green-500' : 'text-yellow-500'}>{testStatus}</span>
                </span>
                <span className={`px-2 py-1 rounded border flex items-center gap-1.5 ${chip}`}>
                  <span className={label}>Size</span>
                  <span className={val}>{formatBytes(baseSize)}</span>
                  <FontAwesomeIcon icon={faExchangeAlt} className="text-gray-500 text-[9px]" />
                  <span className={sizeSimilar ? 'text-yellow-500' : 'text-green-500'}>{formatBytes(testSize)}</span>
                </span>
                <span className={`px-2 py-1 rounded border flex items-center gap-1.5 ${chip}`}>
                  <span className={label}>Δ</span>
                  <span className={sizeSimilar ? 'text-yellow-500' : diff > 0 ? 'text-red-500' : 'text-green-500'}>
                    {diff > 0 ? '+' : ''}{formatBytes(diff)} ({diffPercent > 0 ? '+' : ''}{diffPercent}%)
                  </span>
                </span>
              </div>
            );
          })()}
        </div>

        {/* View Selector */}
        <div className={`px-4 py-2 border-b flex items-center justify-between ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex gap-2">
            <button
              onClick={() => setView('diff')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                view === 'diff'
                  ? 'bg-amber-600 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FontAwesomeIcon icon={faCodeCompare} className="mr-1" />
              Diff
            </button>
            <button
              onClick={() => setView('split')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                view === 'split'
                  ? 'bg-amber-600 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FontAwesomeIcon icon={faExchangeAlt} className="mr-1" />
              Split View
            </button>
            <button
              onClick={() => setView('baseline')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                view === 'baseline'
                  ? 'bg-amber-600 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Baseline Only
            </button>
            <button
              onClick={() => setView('test')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                view === 'test'
                  ? 'bg-amber-600 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Test Only
            </button>
          </div>
          {loading && (
            <FontAwesomeIcon icon={faSync} spin className="text-blue-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} spin className="text-amber-400 text-2xl" />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-red-400 text-sm">
              {error}
            </div>
          ) : view === 'diff' && baselineExchange && testExchange ? (
            <ResponseDiffView
              baseline={baselineExchange}
              test={testExchange}
              baselineLabel={`Baseline · ${baselineName}`}
              testLabel={`Test · ${testName}`}
              isDarkMode={isDarkMode}
            />
          ) : (
            <>
              {(view === 'split' || view === 'baseline') && baselineExchange && (
                <ExchangePanel
                  exchange={baselineExchange}
                  title={`Baseline · ${baselineName}`}
                  titleColor="text-blue-400"
                  isDarkMode={isDarkMode}
                  topLevelTab={sharedTab}
                  onTopLevelTabChange={setSharedTab}
                  viewMode={sharedViewMode}
                  onViewModeChange={setSharedViewMode}
                />
              )}
              {view === 'split' && (
                <div className={`w-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              )}
              {(view === 'split' || view === 'test') && testExchange && (
                <ExchangePanel
                  exchange={testExchange}
                  title={`Test · ${testName}`}
                  titleColor="text-amber-400"
                  isDarkMode={isDarkMode}
                  topLevelTab={sharedTab}
                  onTopLevelTabChange={setSharedTab}
                  viewMode={sharedViewMode}
                  onViewModeChange={setSharedViewMode}
                />
              )}
            </>
          )}
        </div>

        {/* Create Finding form (response panels stay visible above so you can keep
            selecting text and adding excerpts) */}
        {showForm && (
          <div className={`px-4 py-3 border-t space-y-2 overflow-auto max-h-[46%] ${isDarkMode ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faBug} className="text-red-500" />
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Finding title"
                className={`flex-1 px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
              <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rating:</label>
              <select
                value={severity}
                onChange={e => setSeverity(e.target.value)}
                className={`px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="Description — what was accessed and why it's a bypass"
              className={`w-full px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
            {/* Evidence excerpts */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Evidence excerpts ({excerpts.length}):</span>
              <span className={`text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Select text in the response above, then:</span>
              <button
                onMouseDown={e => { e.preventDefault(); addSelection('baseline'); }}
                className={`text-[11px] px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />Baseline excerpt
              </button>
              <button
                onMouseDown={e => { e.preventDefault(); addSelection('test'); }}
                className={`text-[11px] px-2 py-1 rounded ${isDarkMode ? 'bg-amber-900/50 text-amber-300 hover:bg-amber-800' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />Test excerpt
              </button>
            </div>
            {excerpts.length > 0 && (
              <div className="space-y-1">
                {excerpts.map((ex, i) => (
                  <div key={i} className={`flex items-start gap-2 text-[11px] rounded border p-1.5 ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <span className={`px-1 rounded shrink-0 font-semibold ${ex.side === 'baseline' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700') : (isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700')}`}>{ex.side}</span>
                    <pre className={`flex-1 whitespace-pre-wrap font-mono overflow-hidden ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ maxHeight: '3.5em' }}>{ex.content.slice(0, 300)}{ex.content.length > 300 ? '…' : ''}</pre>
                    <button onClick={() => setExcerpts(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 shrink-0">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowForm(false)} disabled={creating} className={`px-3 py-1.5 rounded text-xs font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>Cancel</button>
              <button onClick={submitFinding} disabled={creating || !title.trim()} className="px-3 py-1.5 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                <FontAwesomeIcon icon={creating ? faSpinner : faBug} spin={creating} className="mr-1" />
                {creating ? 'Creating…' : 'Create Finding'}
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className={`px-4 py-3 border-t flex items-center justify-between ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex gap-2 items-center">
            {created ? (
              <span className="text-xs text-green-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faCheckCircle} /> Finding created — visible in the Findings tab
              </span>
            ) : !showForm && (cell.enforcement === 'bypassed' || cell.enforcement === 'partial') && (
              <button
                onClick={openFindingForm}
                className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors"
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                Create Finding
              </button>
            )}
            {onSendToRepeater && cell.exchangeId && (
              <button
                onClick={() => onSendToRepeater(cell.exchangeId)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  isDarkMode ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-800' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <FontAwesomeIcon icon={faRedo} className="mr-1" />
                Send to Repeater
              </button>
            )}
            {onSendToIntruder && cell.exchangeId && (
              <button
                onClick={() => onSendToIntruder(cell.exchangeId)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  isDarkMode ? 'bg-orange-900/40 text-orange-300 hover:bg-orange-800' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                <FontAwesomeIcon icon={faCrosshairs} className="mr-1" />
                Send to Intruder
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthSmashCellModal;
